/**
 * Phase 24.7.3 T2 — Settings section for per-user daily check time.
 *
 * Mirrors TimezoneSection.tsx pattern. Dropdown 00:00–23:00; saves
 * `daily_check_hour: int` to PUT /api/profile.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { updateProfile, getProfile } from "@/api/profile";
import { Card, SectionLabel } from "@/components/ui/Card";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function formatHour(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

export function DailyCheckTimeSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profileQ = useQuery({ queryKey: ["profile"], queryFn: getProfile });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentHour = profileQ.data?.daily_check_hour ?? 5;

  async function handleSave(newHour: number) {
    if (newHour === currentHour) return;
    if (newHour < 0 || newHour > 23) {
      setError(t("settings.daily_check.error_toast"));
      setToast(null);
      return;
    }
    setSaving(true);
    setError(null);
    setToast(null);
    try {
      await updateProfile({ daily_check_hour: newHour });
      await qc.invalidateQueries({ queryKey: ["profile"] });
      setToast(t("settings.daily_check.saved_toast", { hour: formatHour(newHour) }));
    } catch {
      setError(t("settings.daily_check.error_toast"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <SectionLabel>{t("settings.daily_check.title")}</SectionLabel>
      <div className="mt-2 text-[12px] text-taupe">
        {t("settings.daily_check.description")}
      </div>
      <div className="mt-3 text-[14px] text-navy-deep">
        <span className="font-medium">{formatHour(currentHour)}</span>
      </div>
      <div className="mt-3">
        <label className="block text-[12px] text-taupe mb-1">
          {t("settings.daily_check.label_hour")}
        </label>
        <select
          value={currentHour}
          onChange={(e) => handleSave(Number(e.target.value))}
          disabled={saving || profileQ.isLoading}
          className="w-full rounded-lg border border-cream-border bg-cream px-3 py-2 text-[14px] text-navy-deep focus:border-amber focus:outline-none disabled:opacity-60"
        >
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {formatHour(h)}
            </option>
          ))}
        </select>
      </div>
      {error ? (
        <div className="mt-2 rounded-md bg-red-50 px-3 py-1.5 text-[12px] text-red-700">
          {error}
        </div>
      ) : null}
      {toast ? (
        <div className="mt-2 rounded-md bg-amber/10 px-3 py-1.5 text-[12px] text-amber-dark">
          {toast}
        </div>
      ) : null}
    </Card>
  );
}
